/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Feature, FeatureCollection, LineString } from 'geojson';
import { i18n } from '../i18n';

/**
 * Extra knobs you may want to expose later (all optional).
 */
export interface GeojsonToGpxOptions {
  creator?: string;
  metadata?: {
    name?: string;
    time?: string;           // ISO-8601
    desc?: string;
    author?: string;
  };
  trackName?: string;        // Overrides metadata.name when present
  routeName?: string;        // Overrides metadata.name when outputType is rte
  outputType?: 'trk' | 'rte';
  mergeRoutesByProperty?: string; // e.g. "hikeDayNumber"
  singleRoute?: boolean; // when outputType is rte, collapse every line into one <rte> (Garmin-friendly)
  prettyPrint?: boolean;
}

/* ---------- tiny helpers ---------- */
const esc = (s = ''): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const iso = (t: string | number | Date): string => new Date(t).toISOString();

// GraphQL BigInt sends epoch milliseconds as strings ("1697000000000"), which
// `new Date()` can't parse, so read digit-only strings as numbers.
const EPOCH_STRING = /^-?\d+$/;

const maybeIso = (value: string | number | Date | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(typeof value === 'string' && EPOCH_STRING.test(value.trim()) ? Number(value) : value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const prettyPrintXml = (xml: string): string => {
  const normalized = xml.replace(/>\s*</g, '><').trim();
  const withLineBreaks = normalized.replace(/(>)(<)(\/*)/g, '$1\n$2$3');
  const lines = withLineBreaks.split('\n');
  let indentLevel = 0;

  const formatted = lines.map((line) => {
    const token = line.trim();
    if (!token) return '';

    const isClosingTag = /^<\/.+>/.test(token);
    const isDeclaration = /^<\?xml/.test(token);
    const isComment = /^<!--/.test(token);
    const isSelfClosing = /\/>$/.test(token);
    const hasInlineClose = /<\/.+>$/.test(token) && /^<[^/!?][^>]*>/.test(token);

    if (isClosingTag) {
      indentLevel = Math.max(indentLevel - 1, 0);
    }

    const indented = `${'  '.repeat(indentLevel)}${token}`;

    const isOpeningContainer =
      !isDeclaration &&
      !isComment &&
      !isClosingTag &&
      !isSelfClosing &&
      /^<[^/!?][^>]*>$/.test(token) &&
      !hasInlineClose;

    if (isOpeningContainer) {
      indentLevel += 1;
    }

    return indented;
  });

  return `${formatted.filter(Boolean).join('\n')}\n`;
};

/* ---------- metadata builder ---------- */
const buildMeta = (o: GeojsonToGpxOptions): string => {
  if (!o.metadata) return '';
  const { name, time, desc, author } = o.metadata;
  const formattedTime = maybeIso(time);
  return [
    '<metadata>',
    name ? `<name>${esc(name)}</name>` : '',
    formattedTime ? `<time>${formattedTime}</time>` : '',
    desc ? `<desc>${esc(desc)}</desc>` : '',
    author ? `<author><name>${esc(author)}</name></author>` : '',
    '</metadata>',
  ]
    .filter(Boolean)
    .join('');
};

/* ---------- core: segment builder ---------- */
const buildSeg = (
  coords: number[][],
  tStamps?: (string | number | Date | null | undefined)[] | null
): string =>
  [
    '<trkseg>',
    ...coords.map((c, i) => {
      const [lon, lat, ele] = c;
      const ts = maybeIso(tStamps?.[i]);
      return [
        `<trkpt lat="${lat}" lon="${lon}">`,
        typeof ele === 'number' ? `<ele>${ele}</ele>` : '',
        ts ? `<time>${ts}</time>` : '',
        '</trkpt>',
      ].join('');
    }),
    '</trkseg>',
  ].join('');

const buildRoute = (coords: number[][], routeName?: string): string =>
  [
    '<rte>',
    routeName ? `<name>${esc(routeName)}</name>` : '',
    ...coords.map(c => {
      const [lon, lat, ele] = c;
      return [
        `<rtept lat="${lat}" lon="${lon}">`,
        typeof ele === 'number' ? `<ele>${ele}</ele>` : '',
        '</rtept>',
      ].join('');
    }),
    '</rte>',
  ].join('');

// ~1 m. Consecutive legs of one planned route share their junction point, but it
// can come back with float drift, and each leg's elevation is sampled on its own,
// so an exact [lon, lat, ele] match split one day's route into several parts.
const SAME_POINT_TOLERANCE_DEGREES = 1e-5;

const samePoint = (a?: number[], b?: number[]): boolean => {
  if (!a || !b) return false;
  return (
    Math.abs(a[0] - b[0]) <= SAME_POINT_TOLERANCE_DEGREES &&
    Math.abs(a[1] - b[1]) <= SAME_POINT_TOLERANCE_DEGREES
  );
};

const cloneCoordinates = (coords: number[][]): number[][] => coords.map(point => [...point]);

const appendToChunkIfConnected = (chunk: number[][], source: number[][]): boolean => {
  if (!Array.isArray(source) || source.length === 0) return false;
  if (chunk.length === 0) {
    chunk.push(...source);
    return true;
  }

  if (samePoint(chunk[chunk.length - 1], source[0])) {
    chunk.push(...source.slice(1));
    return true;
  }

  // Sometimes segment orientation differs; reverse if needed.
  if (samePoint(chunk[chunk.length - 1], source[source.length - 1])) {
    const reversed = [...source].reverse();
    chunk.push(...reversed.slice(1));
    return true;
  }

  return false;
};

/**
 * Concatenate every line feature, in order, into a single coordinate array.
 * Touching ends are stitched without duplicating the join point; genuinely
 * disconnected segments are still appended so the whole hike ends up as one
 * route. Used for the "single <rte>" (Garmin-compatible) export.
 */
const mergeAllCoordinates = (features: Feature<LineString>[]): number[][] => {
  const merged: number[][] = [];
  features.forEach(feature => {
    const coords = cloneCoordinates(feature.geometry.coordinates);
    if (coords.length === 0) return;
    if (!appendToChunkIfConnected(merged, coords)) {
      merged.push(...coords);
    }
  });
  return merged;
};

type RouteGroup = {
  key: unknown;
  coordinates: number[][];
  chunkIndex: number;
  chunkCount: number;
  dayName?: string;   // the user's own name for the day (hikeDayName)
  chunkName?: string; // the route's own name, when it's unique within the day
};

const presentString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;

const buildRouteGroups = (
  features: Feature<LineString>[],
  mergeProperty?: string
): RouteGroup[] => {
  if (!mergeProperty) {
    return features.map(feature => ({
      key: undefined,
      coordinates: cloneCoordinates(feature.geometry.coordinates),
      chunkIndex: 1,
      chunkCount: 1,
    }));
  }

  type Chunk = { coordinates: number[][]; name?: string; plannedTrailId?: unknown };
  const orderedKeys: string[] = [];
  const grouped = new Map<string, { rawKey: unknown; dayName?: string; chunks: Chunk[] }>();

  features.forEach(feature => {
    const properties = feature?.properties || {};
    const rawKey = properties[mergeProperty];
    const normalizedKey = rawKey === undefined || rawKey === null || rawKey === '' ? '__default' : String(rawKey);

    if (!grouped.has(normalizedKey)) {
      orderedKeys.push(normalizedKey);
      grouped.set(normalizedKey, { rawKey, chunks: [] });
    }

    const bucket = grouped.get(normalizedKey);
    if (!bucket) return;

    bucket.dayName = bucket.dayName ?? presentString(properties.hikeDayName);

    const sourceCoords = cloneCoordinates(feature.geometry.coordinates);
    const plannedTrailId = properties.ptv2PlannedTrailId;
    const lastChunk = bucket.chunks[bucket.chunks.length - 1];

    if (lastChunk && appendToChunkIfConnected(lastChunk.coordinates, sourceCoords)) return;

    // Legs of the same planned route are one route even if their ends don't quite meet.
    const samePlannedTrail = lastChunk && plannedTrailId != null && lastChunk.plannedTrailId === plannedTrailId;
    if (samePlannedTrail) {
      lastChunk.coordinates.push(...sourceCoords);
      return;
    }

    bucket.chunks.push({ coordinates: sourceCoords, name: presentString(properties.name), plannedTrailId });
  });

  const entries: RouteGroup[] = [];
  orderedKeys.forEach(key => {
    const group = grouped.get(key);
    if (!group) return;

    const chunks = group.chunks.filter(chunk => chunk.coordinates.length > 1);
    const nameCounts = new Map<string, number>();
    chunks.forEach(chunk => {
      if (chunk.name) nameCounts.set(chunk.name, (nameCounts.get(chunk.name) ?? 0) + 1);
    });

    chunks.forEach((chunk, idx) => {
      entries.push({
        key: group.rawKey,
        coordinates: chunk.coordinates,
        chunkIndex: idx + 1,
        chunkCount: chunks.length,
        dayName: group.dayName,
        chunkName: chunk.name && nameCounts.get(chunk.name) === 1 ? chunk.name : undefined,
      });
    });
  });

  return entries;
};

const buildRouteName = (
  baseName: string,
  routeGroup: RouteGroup,
  index: number,
  total: number,
  mergeProperty?: string
): string => {
  const { key: routeKey, chunkIndex, chunkCount, dayName, chunkName } = routeGroup;
  const appendPartSuffix = (value: string) => (
    chunkCount > 1 ? i18n.global.t('utils.geojson_to_gpx.part_suffix', { name: value, part: chunkIndex }) : value
  );

  if (total <= 1 && chunkCount <= 1) return baseName;

  if (mergeProperty === 'hikeDayNumber') {
    const dayLabel = routeKey ?? index + 1;

    // Nothing named: keep the generated "<hike> - Day N".
    if (!dayName && !chunkName) {
      return appendPartSuffix(i18n.global.t('utils.geojson_to_gpx.day_name', { name: baseName, day: dayLabel }));
    }

    // "<day name> - <route name>", e.g. "ECH to MUL - Ech to Mul". An unnamed day reads
    // "Day N"; the route half is dropped when there isn't one or it just repeats the day.
    const dayPart = dayName || i18n.global.t('utils.geojson_to_gpx.day_label', { day: dayLabel });
    const routeName = chunkName && chunkName.toLowerCase() !== dayPart.toLowerCase()
      ? i18n.global.t('utils.geojson_to_gpx.day_route_name', { day: dayPart, route: chunkName })
      : dayPart;

    return chunkName ? routeName : appendPartSuffix(routeName);
  }

  if (routeKey !== undefined && routeKey !== null && routeKey !== '') {
    return appendPartSuffix(`${baseName} - ${routeKey}`);
  }

  if (chunkCount > 1 && total <= 1) {
    return i18n.global.t('utils.geojson_to_gpx.part_name', { name: baseName, part: chunkIndex });
  }

  return i18n.global.t('utils.geojson_to_gpx.segment_name', { name: baseName, index: index + 1 });
};

/* ---------------------------------------------------------------------
 *  Public API – drop-in for your existing call *with* timestamp support
 * --------------------------------------------------------------------*/
export function geojsonToGpx(
  // 1️⃣ the GeoJSON
  input: Feature<LineString> | FeatureCollection | LineString,
  // 2️⃣ optional timestamp array – 1-to-1 with coordinates
  timestamps?: (string | number | Date | null | undefined)[] | null,
  // 3️⃣ optional pretties
  options: GeojsonToGpxOptions = {}
): string {
  const creator = options.creator ?? 'HiiKER.com';
  const outputType = options.outputType ?? 'trk';
  const shouldPrettyPrint = options.prettyPrint ?? true;

  /* ---------- flatten every LineString we can see ---------- */
  const features: Feature<LineString>[] = [];
  if ((input as FeatureCollection).type === 'FeatureCollection') {
    (input as FeatureCollection).features.forEach(f => {
      if (f.geometry?.type === 'LineString') features.push(f as Feature<LineString>);
    });
  } else if ((input as Feature<LineString>).type === 'Feature') {
    features.push(input as Feature<LineString>);
  } else if ((input as LineString).type === 'LineString') {
    features.push({ type: 'Feature', properties: {}, geometry: input } as Feature<LineString>);
  } else {
    throw new Error('Unsupported GeoJSON input');
  }

  /* ---------- GPX header ---------- */
  const schemaLocation = 'http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd';
  let gpx = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<gpx version="1.1" creator="${esc(creator)}" xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="${schemaLocation}">`,
    buildMeta(options),
  ].join('\n');

  if (outputType === 'rte') {
    const baseRouteName = options.routeName ?? options.metadata?.name ?? i18n.global.t('utils.geojson_to_gpx.route');

    if (options.singleRoute) {
      const mergedCoordinates = mergeAllCoordinates(features);
      if (mergedCoordinates.length > 1) {
        gpx += buildRoute(mergedCoordinates, baseRouteName);
      }
      gpx += '</gpx>';
      return shouldPrettyPrint ? prettyPrintXml(gpx) : gpx;
    }

    const groupedRoutes = buildRouteGroups(features, options.mergeRoutesByProperty);

    groupedRoutes.forEach((routeGroup, index) => {
      const routeName = buildRouteName(
        baseRouteName,
        routeGroup,
        index,
        groupedRoutes.length,
        options.mergeRoutesByProperty
      );
      gpx += buildRoute(routeGroup.coordinates, routeName);
    });

    gpx += '</gpx>';
    return shouldPrettyPrint ? prettyPrintXml(gpx) : gpx;
  }

  gpx += '<trk>';

  const trkName = options.trackName ?? options.metadata?.name;
  if (trkName) gpx += `<name>${esc(trkName)}</name>`;

  /* ---------- segments ---------- */
  if (features.length === 1) {
    // single linestring → single segment; use timestamps 1-for-1
    gpx += buildSeg(features[0].geometry.coordinates, timestamps);
  } else {
    // multi-segment: we don’t know how you want to slice timestamps,
    // so we simply omit <time> when we can’t be sure
    features.forEach(f => (gpx += buildSeg(f.geometry.coordinates)));
  }

  /* ---------- footer ---------- */
  gpx += '</trk></gpx>';
  return shouldPrettyPrint ? prettyPrintXml(gpx) : gpx;
}
